import styled from 'styled-components';

import RichText from '@/components/RichText/RichText';
import type { Action } from '@/components/SingleImageTile/SingleImageTile';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId } from '@/utils/builderUtils';
import DropdownButton from '@/components/homepage/DropdownButton';
import Button from '@/components/homepage/Button';
import { ITitle } from '../CommonFields';

const Container = styled.div<{ $backgroundImage: string; $mobileBackgroundImage: string }>`
  @media screen and (min-width: 1024px) {
    background-size: cover !important;
    background-position: center;
    background: url(${props => props.$backgroundImage}) no-repeat;
  }

  @media screen and (max-width: 1023px) {
    aspect-ratio: 358 / 270;
    display: flex;
    justify-content: center;
    align-items: center;
    background: url(${props => props.$mobileBackgroundImage}) no-repeat;
    background-size: cover;
    background-position: center;
  }
`;

interface IImageLinkTileProps {
  backgroundImage: string;
  mobileBackgroundImage: string;
  sectionLink: string;
  sectionHeader: {
    optionalLargeHeader: ITitle;
    optionalDescription: string;
  };
  colorText: 'white' | 'black';
  ctas?: Action[];
  margin?: boolean;
  anchorLinkID?: string;
}

export default function ImageLinkTile({
  backgroundImage,
  mobileBackgroundImage,
  sectionLink,
  sectionHeader,
  colorText,
  ctas,
  margin,
  anchorLinkID,
}: IImageLinkTileProps) {
  const actions = ctas?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));

  const moreDescriptiveAriaLabel = (
    headerTitle: string | undefined,
    href: string | undefined,
    label: string
  ): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (headerTitle) {
        return `${headerTitle} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')} page`;
    }
    return label || '';
  };

  const hasDropdown = actions?.some(a => !!a.actions);
  const hasMultipleActions = !!actions && actions?.length > 1;
  const HeaderTag = sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2';
  const headerCustomStyles = {
    h1: 'tw-text-garamond-extralarge-mobile desktop:tw-text-garamond-extralarge-desktop tw-font-normal tw-mb-0',
    h2: 'tw-text-garamond-large-mobile desktop:tw-text-garamond-large-desktop tw-font-normal tw-mb-0',
  };

  return (
    <Container
      className={beTwMerge(
        'tw-mx-4 lg:tw-mx-8 min-[1472px]:tw-w-full tw-max-w-[calc(100%-32px)] min-[1472px]:tw-mx-auto md:tw-max-w-[1440px] tw-pointer-events-auto',
        margin ? 'tw-mb-20' : '',
        colorText === 'black' ? 'tw-text-be-dark' : 'tw-text-white'
      )}
      $backgroundImage={backgroundImage}
      $mobileBackgroundImage={mobileBackgroundImage || backgroundImage}
      {...getAnchorId(anchorLinkID)}
    >
      {sectionLink ? (
        <div className="tw-relative tw-flex tw-flex-col tw-justify-center tw-items-center">
          <a
            href={sectionLink}
            className={beTwMerge(
              'tw-no-underline hover:tw-no-underline focus:tw-no-underline active:tw-no-underline tw-w-full',
              colorText === 'black'
                ? 'tw-text-be-dark hover:tw-text-be-dark-gray focus:tw-text-be-dark-gray active:tw-text-be-dark-gray'
                : 'tw-text-white hover:tw-text-white  focus:tw-text-white  active:tw-text-white'
            )}
          >
            <div
              className={beTwMerge(
                'tw-pt-10 lg:tw-pt-14 tw-pointer-events-auto',
                // eslint-disable-next-line no-nested-ternary
                hasMultipleActions ? 'tw-pb-[100px]' : actions?.length ? 'tw-pb-[84px] lg:tw-pb-[100px]' : ''
              )}
            >
              {/* Section Header */}
              {(!!sectionHeader?.optionalLargeHeader?.content || !!sectionHeader?.optionalDescription) && (
                <div
                  className={beTwMerge(
                    'tw-px-2 md:tw-px-6 lg:tw-max-w-[912px] tw-mx-auto',
                    // eslint-disable-next-line no-nested-ternary
                    sectionHeader?.optionalDescription ? 'tw-mb-10' : 'tw-mb-7'
                  )}
                >
                  {!!sectionHeader?.optionalLargeHeader?.content && (
                    <HeaderTag
                      className={beTwMerge(
                        'tw-text-center hover:!tw-no-underline focus:!tw-no-underline active:!tw-no-underline !tw-no-underline',
                        headerCustomStyles[sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2'],
                        sectionHeader?.optionalDescription && 'tw-mb-4'
                      )}
                    >
                      {sectionHeader?.optionalLargeHeader?.content}
                    </HeaderTag>
                  )}
                  {!!sectionHeader?.optionalDescription && (
                    <RichText
                      className={beTwMerge(
                        'tw-text-center hover:tw-no-uderline focus:tw-no-uderline active:tw-no-uderline',
                        colorText === 'white' ? 'tw-text-white' : 'tw-text-be-dark'
                      )}
                      content={sectionHeader.optionalDescription}
                    />
                  )}
                </div>
              )}
            </div>
          </a>
          {/* Optional CTAs section */}
          <div
            className={beTwMerge(
              'tw-absolute tw-px-2 md:tw-px-6 tw-flex tw-justify-center md:tw-gap-4 tw-pointer-events-auto',
              // eslint-disable-next-line no-nested-ternary
              hasMultipleActions ? 'tw-bottom-14' : 'tw-bottom-10 lg:tw-bottom-14',
              hasDropdown ? 'tw-w-[100%]' : ''
            )}
          >
            {actions?.slice(0, 2).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={`${i}-${label}`}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `md:tw-w-[calc(50%-8px)] tw-text-center [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis tw-pointer-events-auto`
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-max-w-fit tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden tw-pointer-events-auto`,
                    type === 'secondary' && 'tw-leading-[42px]'
                  )}
                  key={`${i}-${label}`}
                  label={label}
                  ariaLabel={moreDescriptiveAriaLabel(sectionHeader?.optionalLargeHeader?.content, href, label)}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className={beTwMerge(
            'tw-py-10 lg:tw-py-14 tw-grid tw-col-span-full tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12 tw-gap-x-4 lg:tw-gap-x-6 tw-pointer-events-auto'
          )}
        >
          {/* Section Header */}
          {(!!sectionHeader?.optionalLargeHeader?.content || !!sectionHeader?.optionalDescription) && (
            <div
              className={beTwMerge(
                'tw-px-2 md:tw-px-6 tw-col-span-full flex flex-col items-center lg:tw-max-w-[912px] tw-mx-auto',
                // eslint-disable-next-line no-nested-ternary
                sectionHeader?.optionalDescription ? 'tw-mb-10' : 'tw-mb-7'
              )}
            >
              {!!sectionHeader?.optionalLargeHeader?.content && (
                <HeaderTag
                  className={beTwMerge(
                    'tw-text-center hover:!tw-no-underline active:!tw-no-underline focus:!tw-no-underline !tw-no-underline',
                    headerCustomStyles[sectionHeader?.optionalLargeHeader?.titleHTMLTag || 'h2'],
                    sectionHeader?.optionalDescription && 'tw-mb-4'
                  )}
                >
                  {sectionHeader?.optionalLargeHeader?.content}
                </HeaderTag>
              )}
              {!!sectionHeader?.optionalDescription && (
                <RichText
                  className={beTwMerge(
                    'tw-text-center hover:tw-no-uderline',
                    colorText === 'white' ? 'tw-text-white' : 'tw-text-be-dark'
                  )}
                  content={sectionHeader.optionalDescription}
                />
              )}
            </div>
          )}

          {/* Optional CTAs section */}
          <div className="tw-col-span-full tw-px-2 md:tw-px-6 tw-flex tw-flex-col md:tw-flex-row tw-justify-center tw-items-center md:tw-gap-4 tw-pointer-events-auto">
            {actions?.slice(0, 2).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={i}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis tw-pointer-events-auto`,
                      i !== actions.length - 1 && 'tw-mb-4 md:tw-mb-0'
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-basis-[calc(50%-8px)] tw-max-w-fit tw-text-center tw-text-ellipsis md:tw-overflow-hidden tw-pointer-events-auto`,
                    i !== actions.length - 1 && 'tw-mb-4 md:tw-mb-0',
                    type === 'secondary' && 'tw-leading-[42px]'
                  )}
                  key={i}
                  label={label}
                  ariaLabel={moreDescriptiveAriaLabel(sectionHeader?.optionalLargeHeader?.content, href, label)}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      )}
    </Container>
  );
}
